/**
 * Obligation summary — cited renderer (Priya/Jordan personas).
 * V6-6-7.
 *
 * Full citations and statute references, accessible behind a "verify"
 * affordance so auditors can trace each obligation back to its source.
 * Renders from the same obligation set payload as the product renderer.
 */

import { useState } from 'react';
import { Badge, Card, EmptyState } from '@aigovops/design-system';
import type { ObligationEntry, ObligationSetPayload } from './ObligationSummaryProduct';

// ============================================================
// Component
// ============================================================

interface ObligationSummaryCitedProps {
  payload: ObligationSetPayload | null;
}

export function ObligationSummaryCited({ payload }: ObligationSummaryCitedProps) {
  if (!payload || payload.obligations.length === 0) {
    return (
      <EmptyState
        title="No obligations"
        description="This system has no applicable obligations yet. Complete classification to compute what applies."
      />
    );
  }

  const grouped = groupByFramework(payload.obligations);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {Object.entries(grouped).map(([framework, obligations]) => (
        <section key={framework}>
          <h3 style={{ margin: '0 0 8px', color: 'var(--text)', fontSize: '1rem' }}>
            {framework}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {obligations.map((ob) => (
              <CitedObligationCard key={ob.obligation_ref} obligation={ob} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ============================================================
// Cited obligation card
// ============================================================

function CitedObligationCard({ obligation }: { obligation: ObligationEntry }) {
  const [showCitation, setShowCitation] = useState(false);

  const hasCitation = Boolean(obligation.citation || obligation.statute_ref);

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Header: ref + title + severity + status */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '0.8125rem',
              color: 'var(--text-dim)',
            }}
          >
            {obligation.obligation_ref}
          </span>
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>
            {obligation.title}
          </span>
          <CitedSeverityBadge severity={obligation.severity} />
          <CitedStatusBadge status={obligation.status} />
        </div>

        {/* Description */}
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          {obligation.plain_description}
        </p>

        {/* Verify affordance — reveals citation + statute */}
        {hasCitation && (
          <>
            <button
              type="button"
              onClick={() => setShowCitation((v) => !v)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--accent)',
                cursor: 'pointer',
                padding: 0,
                fontSize: '0.8125rem',
                textAlign: 'left',
              }}
            >
              {showCitation ? 'Hide citation' : 'Verify source'}
            </button>

            {showCitation && (
              <div
                style={{
                  borderTop: '1px solid var(--border)',
                  paddingTop: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  fontSize: '0.8125rem',
                  color: 'var(--text-dim)',
                }}
              >
                {obligation.statute_ref && (
                  <span>
                    <strong>Statute:</strong> {obligation.statute_ref}
                  </span>
                )}
                {obligation.citation && (
                  <span>
                    <strong>Citation:</strong> {obligation.citation}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

// ============================================================
// Helpers
// ============================================================

function CitedSeverityBadge({ severity }: { severity: ObligationEntry['severity'] }) {
  const isHigh = severity === 'critical' || severity === 'high';
  return <Badge tone={isHigh ? 'accent' : 'neutral'}>{severity}</Badge>;
}

function CitedStatusBadge({ status }: { status: ObligationEntry['status'] }) {
  const map: Record<ObligationEntry['status'], { tone: 'neutral' | 'accent'; label: string }> = {
    met: { tone: 'accent', label: 'Met' },
    unmet: { tone: 'neutral', label: 'Unmet' },
    partial: { tone: 'neutral', label: 'Partial' },
    not_applicable: { tone: 'neutral', label: 'N/A' },
  };
  const { tone, label } = map[status];
  return <Badge tone={tone}>{label}</Badge>;
}

function groupByFramework(
  obligations: ObligationEntry[],
): Record<string, ObligationEntry[]> {
  const result: Record<string, ObligationEntry[]> = {};
  for (const ob of obligations) {
    (result[ob.framework] ??= []).push(ob);
  }
  return result;
}
